import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Alert,
  message,
} from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { authFetch } from '../../utils/api';
import { BrandScopeNotice } from '../../components/BrandScopeNotice';
import {
  canMutateSharedInfra,
  SHARED_INFRA_READONLY_MESSAGE,
} from '../../utils/permissions';

const { Text } = Typography;

type RegistryStatus = 'active' | 'standby' | 'retired';

interface RegistryRecord {
  id: number;
  scope: string;
  regKey: string;
  domain: string;
  certDomain: string | null;
  ipHint: string | null;
  status: RegistryStatus;
  certNotAfter: string | null;
  notes: string | null;
}

interface PreviewRow {
  region: string;
  domain: string;
  matched: boolean;
}

const statusColor: Record<RegistryStatus, string> = {
  active: 'green',
  standby: 'blue',
  retired: 'default',
};

const statusOptions = [
  { label: 'active', value: 'active' },
  { label: 'standby', value: 'standby' },
  { label: 'retired', value: 'retired' },
];

// MVP 只用 region_cert scope（其餘 cdn_decoy / bootstrap / worker_relay 下 wave）。
const scopeOptions = [{ label: 'region_cert（區域憑證域名）', value: 'region_cert' }];

async function apiFetch(url: string, options?: RequestInit) {
  const res = await authFetch(url, options);
  return res.json();
}

/**
 * 基礎設施域名（域名 config 化 MVP，scope=region_cert）。
 *
 * 把 region → cert 子域映射從後端硬編抽到此表，admin 一次改動、節點 5 分鐘內
 * 自動套用（零 code deploy）。查無 / DB 錯時後端 fallback in-code map，不斷下發。
 */
export const InfraDomainTab: React.FC = () => {
  const sharedInfraReadOnly = !canMutateSharedInfra();
  const [data, setData] = useState<RegistryRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RegistryRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<{
    source: string;
    resolved: PreviewRow[];
    fallbackSample: PreviewRow;
  } | null>(null);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiFetch(
        '/api/admin/domain-registry?scope=region_cert'
      );
      if (result.code === 0) {
        setData(Array.isArray(result.data) ? result.data : []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openAdd = () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ scope: 'region_cert', status: 'active' });
    setModalOpen(true);
  };

  const openEdit = (record: RegistryRecord) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const values = await form.validateFields();
    setSaving(true);
    try {
      const url = editing
        ? `/api/admin/domain-registry/${editing.id}`
        : '/api/admin/domain-registry';
      const method = editing ? 'PUT' : 'POST';
      const result = await apiFetch(url, {
        method,
        body: JSON.stringify(values),
      });
      if (result.code === 0) {
        message.success(editing ? '域名已更新' : '域名已新增');
        setModalOpen(false);
        fetchData();
      } else {
        message.error(result.msg || '操作失敗');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (sharedInfraReadOnly) {
      message.warning(SHARED_INFRA_READONLY_MESSAGE);
      return;
    }
    const result = await apiFetch(`/api/admin/domain-registry/${id}`, {
      method: 'DELETE',
    });
    if (result.code === 0) {
      message.success('域名已刪除');
      fetchData();
    } else {
      message.error(result.msg || '刪除失敗');
    }
  };

  const openPreview = async () => {
    const result = await apiFetch('/api/admin/domain-registry/preview');
    if (result.code === 0) {
      setPreview(result.data);
      setPreviewOpen(true);
    } else {
      message.error(result.msg || '預覽失敗');
    }
  };

  return (
    <div>
      <Alert
        type="info"
        message="region → 憑證域名一次改動"
        description="下方每筆是「區域代碼 → 節點 anytls/trojan/hy2 憑證子域」的映射。改完存檔，節點 5 分鐘內自動套用（零 code deploy）。查無或後端讀取失敗時會自動 fallback 程式內建映射，不會中斷下發。reg_key 用區域代碼（hk / au / uk / tp / sel / bkk / sgp），_default 為未對應區域的保底值。"
        style={{ marginBottom: 12 }}
      />
      <BrandScopeNotice kind="shared-infra" style={{ marginBottom: 12 }} />

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Button icon={<EyeOutlined />} onClick={openPreview}>
          預覽解析結果
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={sharedInfraReadOnly}
          onClick={openAdd}
        >
          新增域名
        </Button>
      </div>

      <Table
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1000 }}
        pagination={{ pageSize: 50 }}
      >
        <Table.Column dataIndex="id" title="ID" width={70} />
        <Table.Column
          dataIndex="regKey"
          title="區域代碼"
          width={120}
          render={(v: string) => <Text code>{v}</Text>}
        />
        <Table.Column dataIndex="domain" title="憑證域名" width={240} />
        <Table.Column
          dataIndex="certDomain"
          title="cert SAN"
          width={200}
          render={(v: string | null) => v || '-'}
        />
        <Table.Column
          dataIndex="status"
          title="狀態"
          width={100}
          render={(status: RegistryStatus) => (
            <Tag color={statusColor[status]}>{status}</Tag>
          )}
        />
        <Table.Column
          dataIndex="notes"
          title="備註"
          width={200}
          ellipsis
          render={(v: string | null) => v || '-'}
        />
        <Table.Column
          title="操作"
          width={180}
          fixed="right"
          render={(_: unknown, record: RegistryRecord) => (
            <Space wrap>
              <Button
                size="small"
                disabled={sharedInfraReadOnly}
                onClick={() => openEdit(record)}
              >
                編輯
              </Button>
              <Popconfirm
                title="確定刪除此域名映射？"
                onConfirm={() => handleDelete(record.id)}
              >
                <Button size="small" danger disabled={sharedInfraReadOnly}>
                  刪除
                </Button>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>

      <Modal
        title={
          editing
            ? `編輯域名映射：${editing.regKey}`
            : '新增基礎設施域名映射'
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="scope" label="範疇（scope）">
            <Select options={scopeOptions} disabled />
          </Form.Item>
          <Form.Item
            name="regKey"
            label="區域代碼（reg_key）"
            rules={[
              { required: true, message: '請輸入區域代碼' },
              {
                pattern: /^[a-zA-Z0-9_]+$/,
                message: '只允許英數字與底線（如 hk / au / _default）',
              },
            ]}
          >
            <Input placeholder="hk / au / uk / tp / sel / bkk / sgp / _default" />
          </Form.Item>
          <Form.Item
            name="domain"
            label="憑證域名（下發用）"
            rules={[
              { required: true, message: '請輸入域名' },
              {
                pattern: /^[a-zA-Z0-9.-]+$/,
                message: '域名只允許英數字、點與連字號',
              },
            ]}
          >
            <Input placeholder="endpoint.swiftmesh.net" />
          </Form.Item>
          <Form.Item
            name="certDomain"
            label="cert SAN 域名（選填，留空=同域名）"
            rules={[
              {
                pattern: /^[a-zA-Z0-9.-]+$/,
                message: '域名只允許英數字、點與連字號',
              },
            ]}
          >
            <Input placeholder="留空即與憑證域名相同" />
          </Form.Item>
          <Form.Item name="ipHint" label="IP 提示（選填，僅運維參考不下發）">
            <Input placeholder="18.162.167.29" />
          </Form.Item>
          <Form.Item name="status" label="狀態">
            <Select options={statusOptions} />
          </Form.Item>
          <Form.Item name="notes" label="備註">
            <Input.TextArea rows={2} placeholder="人類可讀說明" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="解析結果預覽（節點實際會拿到的憑證域名）"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={560}
      >
        {preview && (
          <>
            <Alert
              type={preview.source === 'registry' ? 'success' : 'warning'}
              message={
                preview.source === 'registry'
                  ? '來源：後台登錄表（registry）'
                  : '來源：程式內建映射（fallback）— 表為空或讀取失敗'
              }
              style={{ marginBottom: 12 }}
            />
            <Table
              dataSource={[
                ...preview.resolved,
                { ...preview.fallbackSample },
              ].map((r, i) => ({ ...r, _k: i }))}
              rowKey="_k"
              pagination={false}
              size="small"
            >
              <Table.Column
                dataIndex="region"
                title="區域"
                render={(v: string) => <Text code>{v}</Text>}
              />
              <Table.Column dataIndex="domain" title="解析域名" />
              <Table.Column
                dataIndex="matched"
                title="命中"
                width={90}
                render={(m: boolean) =>
                  m ? (
                    <Tag color="green">命中</Tag>
                  ) : (
                    <Tag color="orange">fallback</Tag>
                  )
                }
              />
            </Table>
          </>
        )}
      </Modal>
    </div>
  );
};
